// Liqo — utility multipiattaforma (web vs app nativa Capacitor iOS/Android).
//
// Problema risolto: in un'app nativa la WebView ha origin `capacitor://localhost`,
// quindi i provider OAuth (Google/Emergent, Enable Banking) non possono reindirizzare
// lì. Soluzione: in nativo apriamo il login nel browser di sistema e usiamo come
// redirect il DOMINIO PUBBLICO (host del backend) associato ai Universal/App Link;
// al ritorno l'OS riapre l'app e ci consegna l'URL via `appUrlOpen` (deep link).
//
// Sul WEB tutto resta IDENTICO a prima (redirect pieno su window.location).

// Parsing PURO dei callback (testabile in isolamento): vedi lib/deeplink.js.
export { isBankCallback, parseBankCallback, parseSessionId } from "./deeplink";

// I plugin Capacitor sono importati in modo lazy: sul web non vengono nemmeno caricati.
let _App = null;
let _Browser = null;

export const isNativePlatform = () =>
  !!(
    typeof window !== "undefined" &&
    window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform()
  );

// Base per i redirect OAuth.
// - web: origin corrente (comportamento attuale, invariato)
// - nativo: origin del dominio pubblico (host del backend) associato ai deep link
export const getRedirectBase = () => {
  if (isNativePlatform()) {
    try {
      return new URL(process.env.REACT_APP_BACKEND_URL).origin;
    } catch {
      return window.location.origin;
    }
  }
  return window.location.origin;
};

// Apre un URL di autenticazione.
// - web: redirect pieno nella stessa scheda (come prima)
// - nativo: scheda di sistema (così i Universal/App Link possono riaprire l'app)
export const openAuthUrl = async (url) => {
  if (isNativePlatform()) {
    try {
      if (!_Browser) ({ Browser: _Browser } = await import("@capacitor/browser"));
      await _Browser.open({ url });
      return;
    } catch (e) {
      // Fallback al redirect classico se il browser di sistema non è disponibile.
      console.warn("[Liqo] apertura browser nativo fallita, uso redirect:", e);
    }
  }
  window.location.href = url;
};

// Chiude l'eventuale scheda di sistema aperta per il login (solo nativo).
export const closeAuthBrowser = async () => {
  if (!isNativePlatform()) return;
  try {
    if (!_Browser) ({ Browser: _Browser } = await import("@capacitor/browser"));
    await _Browser.close();
  } catch (e) {
    console.warn("[Liqo] chiusura browser nativo fallita:", e);
  }
};

// Registra un handler per i deep link in ingresso (appUrlOpen). Ritorna un cleanup.
// Sul web è un no-op.
export const onDeepLink = (handler) => {
  if (!isNativePlatform()) return () => {};
  let remove = () => {};
  (async () => {
    try {
      if (!_App) ({ App: _App } = await import("@capacitor/app"));
      const sub = await _App.addListener("appUrlOpen", (event) => {
        if (event && event.url) handler(event.url);
      });
      remove = () => {
        try {
          sub.remove();
        } catch (e) {
          console.warn("[Liqo] rimozione listener deep link fallita:", e);
        }
      };
    } catch (e) {
      console.warn("[Liqo] registrazione deep link (appUrlOpen) fallita:", e);
    }
  })();
  return () => remove();
};
